
// frontend/src/services/inssCalculator.ts

// --- Interfaces ---
export interface InssObraInputs {
  vau: number;
  areaPrincipal: number;
  areaComplementarCoberta: number;
  areaComplementarDescoberta: number;
  destinacao: string;
  categoria: string;
  tipo: string; // "Alvenaria", "Madeira", "Mista"
  pjOptanteSimples: boolean;
}

export interface InssObraResult {
  inputs: InssObraInputs & { areaTotal: number };
  calculoBase: {
    vauAplicado: number;
    custoObraCalculado: number;
    rmtBaseAfericao: number;
  };
  comparativoDebitos: {
    pessoaJuridica: {
      descricao: string;
      rmtCalculada: number;
      aliquotaAplicada: number;
      debitoAfericaoSero: number;
    };
    pessoaFisicaFatorSocial: {
      descricao: string;
      rmtCalculada: number;
      aliquotaAplicada: number;
      debitoAfericaoSero: number;
    };
    pessoaFisicaFatorAjuste: {
      descricao: string;
      rmtMinimaNecessaria: number;
      aliquotaAplicada: number;
      debitoMinimoRecolhido: number;
    };
  };
  observacoes: string[];
}

// --- Constantes e Tabelas de Fatores (Baseado no Manual Sero v3.0) ---

const PCT_REDUCAO_COMP = {
  COBERTA: 0.50, // (redução de 50%)
  DESCOBERTA: 0.25 // (redução de 75%)
};

const PCT_MAO_OBRA: { [key: string]: number } = {
  "Alvenaria": 0.20,
  "Madeira": 0.15,
  "Mista": 0.15,
  "Casa Popular Alvenaria": 0.12,
  "Casa Popular Madeira": 0.07,
  "Casa Popular Mista": 0.07,
};

const PCT_CATEGORIA: { [key: string]: number } = {
  "Obra Nova": 1.00,
  "Acréscimo": 1.00,
  "Reforma": 0.35,
  "Demolição": 0.10,
};

const PCT_DESTINACAO: { [key: string]: number } = {
  "Edifício de Garagens": 0.80,
  // Outras destinações têm fator 1.00 (implícito)
};

const ALIQUOTA_PADRAO = 0.368; // (Total: 20% PAT + 8% SEG + 3% GILRAT + 5.8% Terceiros)
const ALIQUOTA_SIMPLES = 0.31; // (Total: 20% PAT + 8% SEG + 3% GILRAT; isento de Terceiros)

// --- Funções de Lógica Auxiliares ---

/**
 * Retorna o fator de equivalência com base na destinação e área principal.
 * Baseado na Subseção 17.1 do Manual Sero v3.0.
 */
const getPercentualEquivalencia = (destinacao: string, areaPrincipal: number): number => {
  switch (destinacao) {
    case "Residencial Unifamiliar":
      return areaPrincipal <= 1000 ? 0.89 : 0.85;
    case "Residencial Multifamiliar":
      return areaPrincipal <= 1000 ? 0.90 : 0.86;
    case "Comercial Salas e Lojas":
      return 0.90;
    case "Galpão Industrial":
      return 0.95;
    case "Casa Popular":
      return 0.70;
    case "Edifício de Garagens":
      return 0.80;
    case "Comercial Bancos":
      return 1.00; // Exemplo, ajustar conforme manual
    case "Comercial Hotéis":
      return 0.90; // Exemplo, ajustar conforme manual
    case "Serviços de Saúde":
      return 0.90; // Exemplo, ajustar conforme manual
    case "Templos":
      return 0.85; // Exemplo, ajustar conforme manual
    case "Escolas":
      return 0.85; // Exemplo, ajustar conforme manual
    case "Clubes e Centros Esportivos":
      return 0.80; // Exemplo, ajustar conforme manual
    case "Obras Complementares":
      return 0.70; // Exemplo, ajustar conforme manual
    default:
      return 1.00; // Fator padrão para destinações não especificadas
  }
};

/**
 * Retorna o fator social correto com base na área total.
 * Baseado no Manual Sero v3.0.
 */
const getFatorSocial = (areaTotal: number): number => {
  if (areaTotal <= 100) return 0.20;
  if (areaTotal <= 200) return 0.40;
  if (areaTotal <= 300) return 0.55;
  if (areaTotal <= 400) return 0.70;
  return 0.90; // Para áreas > 400
};

/**
 * Retorna o percentual mínimo de RMT necessário para o Fator de Ajuste.
 * Baseado no Manual Sero v3.0.
 */
const getPercentualMinimoFatorAjuste = (areaTotal: number): number => {
  return areaTotal <= 350 ? 0.50 : 0.70;
};

// --- Função Principal ---
export const calculateInssObra = (inputs: InssObraInputs): InssObraResult => {
  const { vau, areaPrincipal, areaComplementarCoberta, areaComplementarDescoberta, destinacao, categoria, tipo, pjOptanteSimples } = inputs;

  // Passo 1: Desestruturar Entradas (já feito acima)
  const areaTotal = areaPrincipal + areaComplementarCoberta + areaComplementarDescoberta;

  // Passo 2: Calcular Custo da Obra (COD)
  const pctEquiv = getPercentualEquivalencia(destinacao, areaPrincipal);
  const areaCalcPrincipal = areaPrincipal * pctEquiv;
  const areaCalcComp = (areaComplementarCoberta * PCT_REDUCAO_COMP.COBERTA) + (areaComplementarDescoberta * PCT_REDUCAO_COMP.DESCOBERTA);
  const cod = (areaCalcPrincipal + areaCalcComp) * vau;

  // Passo 3: Calcular RMT Base (RMT Não Decadente)
  let tipoCalculo = tipo;
  if (destinacao === "Casa Popular") {
    tipoCalculo = `Casa Popular ${tipo}`;
  }
  const pctMO = PCT_MAO_OBRA[tipoCalculo] || 0; // Garante 0 se o tipo não for encontrado
  const rmtBruta = cod * pctMO;
  const pctCat = PCT_CATEGORIA[categoria] || 1.00; // Garante 1.00 se a categoria não for encontrada
  const pctDest = PCT_DESTINACAO[destinacao] || 1.0;
  const rmtBase = rmtBruta * pctCat * pctDest; // (RMT Não Decadente, base para todos os cálculos)

  // Passo 4: Calcular os 3 Cenários
  // Cenário 1 (PJ):
  const aliquotaPJ = pjOptanteSimples ? ALIQUOTA_SIMPLES : ALIQUOTA_PADRAO;
  const debitoPJ = rmtBase * aliquotaPJ;

  // Cenário 2 (PF - Fator Social):
  const pctFS = getFatorSocial(areaTotal);
  const rmtPF_FS = rmtBase * pctFS;
  const debitoPF_FS = rmtPF_FS * ALIQUOTA_PADRAO;

  // Cenário 3 (PF - Fator de Ajuste Mínimo):
  const pctMinFA = getPercentualMinimoFatorAjuste(areaTotal);
  const rmtMinimaFA = rmtPF_FS * pctMinFA; // Ajuste aqui
  const debitoMinimoFA = rmtMinimaFA * 0.20; // Alíquota de 20% inferida do site para este cenário

  // Formato da Saída (Objeto de Retorno)
  return {
    inputs: { ...inputs, areaTotal },
    calculoBase: {
      vauAplicado: isNaN(vau) || !isFinite(vau) ? 0 : vau,
      custoObraCalculado: isNaN(cod) || !isFinite(cod) ? 0 : cod,
      rmtBaseAfericao: isNaN(rmtBase) || !isFinite(rmtBase) ? 0 : rmtBase,
    },
    comparativoDebitos: {
      pessoaJuridica: {
        descricao: "Cálculo PJ (Aferição Indireta, Sem Fator Social/Ajuste)",
        rmtCalculada: isNaN(rmtBase) || !isFinite(rmtBase) ? 0 : rmtBase,
        aliquotaAplicada: aliquotaPJ,
        debitoAfericaoSero: isNaN(debitoPJ) || !isFinite(debitoPJ) ? 0 : debitoPJ,
      },
      pessoaFisicaFatorSocial: {
        descricao: "Cálculo PF (Com benefício do Fator Social)",
        rmtCalculada: isNaN(rmtPF_FS) || !isFinite(rmtPF_FS) ? 0 : rmtPF_FS,
        aliquotaAplicada: ALIQUOTA_PADRAO,
        debitoAfericaoSero: isNaN(debitoPF_FS) || !isFinite(debitoPF_FS) ? 0 : debitoPF_FS,
      },
      pessoaFisicaFatorAjuste: {
        descricao: "Valor MÍNIMO de recolhimento (via eSocial/DCTFWeb) para ZERAR o débito da aferição",
        rmtMinimaNecessaria: isNaN(rmtMinimaFA) || !isFinite(rmtMinimaFA) ? 0 : rmtMinimaFA,
        aliquotaAplicada: 0.20,
        debitoMinimoRecolhido: isNaN(debitoMinimoFA) || !isFinite(debitoMinimoFA) ? 0 : debitoMinimoFA,
      },
    },
    observacoes: [
      "O cenário 'Pessoa Jurídica' representa o custo caso a empresa não possua contabilidade regular.",
      "O cenário 'Pessoa Física (Fator de Ajuste)' não é o débito final da aferição, mas sim o valor total de INSS (20%) sobre a RMT mínima (50% ou 70% da RMT Base) que precisa ser declarada e paga ao longo da obra (via eSocial/DCTFWeb) para que o débito da aferição seja zerado.",
      "Estes cálculos são simulações (usando o VAU informado pelo usuário) e não substituem a aferição oficial no Sero.",
    ],
  };
};
